import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Plus, Inbox, Send, Gavel } from "lucide-react";
import { contractsApi, formatApiError } from "../../lib/ledgerApi";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import ContractStateBadge from "../../components/ContractStateBadge";

const TYPE_LABEL = {
  escrow: "Escrow",
  streaming: "Streaming",
  conditional: "Conditional",
  invoice: "Invoice-linked",
};

const fmt = (s) => {
  try {
    const n = Number(s);
    return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 6 }) : s;
  } catch { return s; }
};

export default function Contracts() {
  const { business } = useAuth();
  const [tab, setTab] = useState("any");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ state: "", contract_type: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = { role: tab };
      if (filter.state) params.state = filter.state;
      if (filter.contract_type) params.contract_type = filter.contract_type;
      const r = await contractsApi.list(params);
      setItems(r);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [tab, filter.state, filter.contract_type]); // eslint-disable-line

  const counts = useMemo(() => {
    const c = { any: 0, creator: 0, counterparty: 0, arbiter: 0 };
    for (const x of items) c.any += 1;
    return c;
  }, [items]);

  const TABS = [
    { key: "any", label: "All", icon: Inbox },
    { key: "creator", label: "As creator", icon: Send },
    { key: "counterparty", label: "As counterparty", icon: Inbox },
    { key: "arbiter", label: "As arbiter", icon: Gavel },
  ];

  return (
    <div className="max-w-7xl px-6 md:px-10 py-10 md:py-14" data-testid="contracts-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Smart contracts</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
            <FileText className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} /> Contracts
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Programmable payments — escrow, streaming, conditional, invoice-linked. Funds lock on the ledger; the
            evaluator releases on rules. AI never executes anything.
          </p>
        </div>
        <Link
          to="/app/contracts/new"
          data-testid="contracts-new-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-foreground bg-foreground text-background text-sm hover:opacity-90"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} /> New contract
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest border rounded-sm transition-colors ${active ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"}`}
            >
              <Icon className="w-3 h-3" strokeWidth={1.5} /> {t.label}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2 text-xs">
          <select
            value={filter.state}
            onChange={(e) => setFilter((f) => ({ ...f, state: e.target.value }))}
            data-testid="filter-state"
            className="bg-card border border-border rounded-sm px-2 py-1 font-mono"
          >
            <option value="">All states</option>
            {["draft", "active", "fulfilled", "disputed", "cancelled", "refunded", "expired", "blocked"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={filter.contract_type}
            onChange={(e) => setFilter((f) => ({ ...f, contract_type: e.target.value }))}
            data-testid="filter-type"
            className="bg-card border border-border rounded-sm px-2 py-1 font-mono"
          >
            <option value="">All types</option>
            {["escrow", "streaming", "conditional", "invoice"].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="border border-border rounded-sm bg-card p-8 text-center text-muted-foreground text-sm">
          No contracts yet. Click <Link to="/app/contracts/new" className="underline">New contract</Link> to draft your first one.
        </div>
      )}
      <div className="border border-border rounded-sm bg-card divide-y divide-border">
        {items.map((c) => {
          const isCreator = c.creator_business_id === business?.id;
          const counterpartyName = isCreator ? c.counterparty_legal_name : c.creator_legal_name;
          return (
            <Link
              key={c.id}
              to={`/app/contracts/${c.id}`}
              data-testid={`contract-row-${c.id}`}
              className="grid grid-cols-12 gap-3 p-4 items-center hover:bg-muted/40 transition-colors"
            >
              <div className="col-span-2"><ContractStateBadge state={c.state} /></div>
              <div className="col-span-2 text-[11px] uppercase tracking-widest text-muted-foreground">{TYPE_LABEL[c.contract_type] || c.contract_type}</div>
              <div className="col-span-3 truncate">
                <span className="text-muted-foreground">{isCreator ? "to" : "from"}</span>{" "}
                <span className="font-medium">{counterpartyName || "—"}</span>
              </div>
              <div className="col-span-2 font-mono text-sm">
                <span className="text-status-info">{fmt(c.total_locked_amount)}</span>{" "}
                <span className="text-muted-foreground">{c.currency}</span>
              </div>
              <div className="col-span-2 text-[11px] font-mono text-muted-foreground">
                released {fmt(c.released_amount)}
              </div>
              <div className="col-span-1 text-[11px] font-mono text-muted-foreground text-right">
                {new Date(c.created_at).toLocaleDateString()}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
